import { useSearchParams } from "react-router-dom";
import { useFilterOptions, useMetaKeys } from "@/lib/api/queries";
import {
  activeFilterCount,
  type Filters,
  parseFilters,
  RANGE_PRESETS,
  type RangePreset,
  serializeFilters,
  toggleValue,
} from "@/lib/filters";
import { Icon } from "./icon/Icon";
import { Button, Tag, TextField } from "./ui";

/**
 * The shared filter bar. State lives in the URL, so it survives reloads and
 * every view is shareable.
 *
 * The dropdown contents — including the *custom field* keys and their values —
 * come from `/v1/meta/filters` and `/v1/meta/keys`, i.e. from whatever the
 * reporting services actually sent. Nothing here is hardcoded per product.
 */

export function useFilters(): [Filters, (next: Filters) => void] {
  const [params, setParams] = useSearchParams();
  const filters = parseFilters(params);
  const setFilters = (next: Filters) => setParams(serializeFilters(next), { replace: true });
  return [filters, setFilters];
}

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-caption-1 font-semibold text-label-alternative">{label}</span>
      {options.map((option) => {
        const active = selected.includes(option);
        return (
          <button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            aria-pressed={active}
            className="rounded-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <Tag color={active ? "primary" : "neutral"} variant={active ? "solid" : "weak"} size="small">
              {option}
            </Tag>
          </button>
        );
      })}
    </div>
  );
}

export function FilterBar() {
  const [filters, setFilters] = useFilters();
  const options = useFilterOptions(filters);
  const metaKeys = useMetaKeys(filters);
  const active = activeFilterCount(filters);

  const setRange = (range: RangePreset) => setFilters({ ...filters, range });

  return (
    <section
      aria-label="공통 필터"
      className="sticky top-0 z-10 flex flex-col gap-3 border-b border-line-alternative bg-background-normal/95 px-4 py-3 backdrop-blur md:px-6"
    >
      <div className="flex flex-wrap items-center gap-2">
        <fieldset className="flex items-center gap-1 border-0 p-0" aria-label="기간">
          {(Object.keys(RANGE_PRESETS) as RangePreset[]).map((preset) => (
            <Button
              key={preset}
              size="small"
              variant={filters.range === preset ? "solid" : "outlined"}
              color={filters.range === preset ? "primary" : "assistive"}
              onClick={() => setRange(preset)}
            >
              {RANGE_PRESETS[preset].label}
            </Button>
          ))}
        </fieldset>

        <div className="ml-auto flex items-center gap-2">
          <TextField
            size="medium"
            iconLeft="search"
            placeholder="request/trace/user 검색"
            value={filters.q ?? ""}
            onChange={(event) => setFilters({ ...filters, q: event.target.value || undefined })}
            style={{ width: 240 }}
          />
          {active > 0 && (
            <Button
              size="small"
              variant="text"
              color="assistive"
              iconLeft="close"
              onClick={() =>
                setFilters({
                  ...filters,
                  service_id: [],
                  model: [],
                  provider: [],
                  env: [],
                  operation: [],
                  status: undefined,
                  q: undefined,
                  meta: {},
                })
              }
            >
              필터 {active}개 지우기
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <ChipGroup
          label="서비스"
          options={options.data?.services ?? []}
          selected={filters.service_id}
          onToggle={(value) => setFilters({ ...filters, service_id: toggleValue(filters.service_id, value) })}
        />
        <ChipGroup
          label="모델"
          options={options.data?.models ?? []}
          selected={filters.model}
          onToggle={(value) => setFilters({ ...filters, model: toggleValue(filters.model, value) })}
        />
        <ChipGroup
          label="환경"
          options={options.data?.environments ?? []}
          selected={filters.env}
          onToggle={(value) => setFilters({ ...filters, env: toggleValue(filters.env, value) })}
        />

        {/* Custom fields — discovered from the data, so a service can add a
            dimension and it shows up here without a frontend change. */}
        {metaKeys.data?.keys.map((meta) => (
          <div key={meta.key} className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-caption-1 font-semibold text-label-alternative">
              <Icon name="tag" size={12} />
              {meta.key}
            </span>
            {meta.values.map((value) => {
              const selected = filters.meta[meta.key] === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    const next = { ...filters.meta };
                    if (selected) {
                      delete next[meta.key];
                    } else {
                      next[meta.key] = value;
                    }
                    setFilters({ ...filters, meta: next });
                  }}
                  className="rounded-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <Tag color={selected ? "violet" : "neutral"} variant={selected ? "solid" : "weak"} size="small">
                    {value}
                  </Tag>
                </button>
              );
            })}
            {meta.truncated && <span className="text-caption-1 text-label-assistive">…</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
